import React from 'react';
import { View, StyleSheet, Image } from 'react-native';
import { RESIDENTS_SVG_COMPONENTS } from '@/assets/residents';
import { useThemeColors } from '@/hooks/residents/useThemeColors';
import {
  NOTIFICATION_AVATAR,
  NOTIFICATION_AVATAR_DESKTOP,
  NOTIFICATION_UNREAD_DOT,
  NOTIFICATION_UNREAD_DOT_DESKTOP,
} from '@/constants/residents/mobile/notificationColors';

interface NotificationAvatarProps {
  showUnreadDot?: boolean;
  size?: number;
  iconSize?: number;
  variant?: 'property' | 'marketplace' | 'message';
  imageUrl?: string | null;
  platform?: 'mobile' | 'desktop';
}

export const NotificationAvatar: React.FC<NotificationAvatarProps> = ({
  showUnreadDot = false,
  size,
  iconSize,
  variant = 'property',
  imageUrl,
  platform = 'mobile',
}) => {
  const { colors } = useThemeColors();

  const isDesktop = platform === 'desktop';
  const avatarSize = size ?? (isDesktop ? NOTIFICATION_AVATAR_DESKTOP.size : NOTIFICATION_AVATAR.size);
  const iconSizeValue = iconSize ?? (isDesktop ? 18 : NOTIFICATION_AVATAR.iconSize);
  const borderRadius = avatarSize / 2;

  const IconComponent = variant === 'message'
    ? RESIDENTS_SVG_COMPONENTS.CHAT_ICON
    : variant === 'marketplace'
      ? RESIDENTS_SVG_COMPONENTS.SUPERMARKET
      : RESIDENTS_SVG_COMPONENTS.PROPERTY;

  const avatarBackgroundColor = isDesktop
    ? colors.activityIcon.bg
    : colors.glass.buttonBg;

  const iconColor = colors.text.muted;

  const unreadDotStyle = isDesktop
    ? {
        position: 'absolute' as const,
        top: NOTIFICATION_UNREAD_DOT_DESKTOP.offsetTop,
        left: NOTIFICATION_UNREAD_DOT_DESKTOP.offsetLeft,
        width: NOTIFICATION_UNREAD_DOT_DESKTOP.size,
        height: NOTIFICATION_UNREAD_DOT_DESKTOP.size,
        borderRadius: NOTIFICATION_UNREAD_DOT_DESKTOP.borderRadius,
        backgroundColor: colors.accent.red,
        borderWidth: 1,
        borderColor: colors.accent.red,
      }
    : {
        ...styles.unreadDot,
        backgroundColor: colors.accent.red,
      };

  return (
    <View style={styles.container}>
      <View style={[
        styles.avatar,
        {
          width: avatarSize,
          height: avatarSize,
          borderRadius,
          backgroundColor: avatarBackgroundColor,
          borderWidth: 1,
          borderColor: colors.activityIcon.border,
        },
      ]}>
        {imageUrl ? (
          <Image
            source={{ uri: imageUrl }}
            style={[styles.image, { width: avatarSize, height: avatarSize, borderRadius }]}
            resizeMode="cover"
          />
        ) : (
          <IconComponent
            width={iconSizeValue}
            height={iconSizeValue}
            color={iconColor}
          />
        )}
      </View>
      {showUnreadDot && <View style={unreadDotStyle} accessibilityLabel="Unread notification" />}
    </View>
  );
};

const styles = StyleSheet.create({
  container: {
    position: 'relative',
  },
  avatar: {
    justifyContent: 'center',
    alignItems: 'center',
    overflow: 'hidden',
  },
  image: {
    width: '100%',
    height: '100%',
  },
  unreadDot: {
    position: 'absolute',
    top: 0,
    left: 0,
    width: NOTIFICATION_UNREAD_DOT.size,
    height: NOTIFICATION_UNREAD_DOT.size,
    borderRadius: NOTIFICATION_UNREAD_DOT.borderRadius,
  },
});

export default NotificationAvatar;
